/**
 * 🎯 Unified Video State
 * 動画システムの統一状態管理（3つの状態管理を1つに統合）
 *
 * 統合対象:
 * - VideoSystemState (動画情報)
 * - TransitionStateManager (トランジション状態)
 * - BackgroundTransitionOrchestrator (スケジューラ状態)
 */

import { logger } from '@/utils/logger.js';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * 動画タイプ
 */
export type VideoType = 'background' | 'gift' | 'transition';

/**
 * 動画要素識別子
 */
export type VideoElement = 'A' | 'B';

/**
 * トランジションタイプ
 */
export type TransitionType =
  | 'fade'
  | 'crossfade'
  | 'slide'
  | 'zoom'
  | 'switch-random-overlap'
  | 'switch-to-gift'
  | 'switch-from-gift'
  | null;

/**
 * トランジション状態（TransitionStateManagerから統合）
 */
export type TransitionPhase =
  | 'idle'
  | 'initializing'
  | 'loading'
  | 'playing'
  | 'completed'
  | 'error'
  | 'fallback';

/**
 * スケジューラ状態（BackgroundTransitionOrchestratorから統合）
 */
export type SchedulerState = 'idle' | 'preparing' | 'active' | 'paused';

/**
 * 動画メタデータ
 */
export interface VideoMetadata {
  duration?: number;
  width?: number;
  height?: number;
  format?: string;
  [key: string]: unknown;
}

/**
 * 動画情報
 */
export interface VideoInfo {
  src: string | null;
  type: VideoType | null;
  element: VideoElement | null;
  startTime: number | null;
  metadata?: VideoMetadata;
}

/**
 * プリロード動画情報
 */
export interface PreloadInfo {
  src: string | null;
  type: VideoType | null;
  element: VideoElement | null;
  ready: boolean;
  metadata?: VideoMetadata;
}

/**
 * トランジション状態（統合）
 */
export interface TransitionState {
  /** トランジションフェーズ */
  phase: TransitionPhase;
  /** トランジションタイプ */
  type: TransitionType;
  /** 進行度 (0-1) */
  progress: number;
  /** 開始時刻 */
  startTime: number | null;
  /** 現在のトランジション名 */
  name: string | null;
  /** エラー情報 */
  error: Error | null;
}

/**
 * スケジューラ状態（統合）
 */
export interface SchedulerInfo {
  /** スケジューラ状態 */
  state: SchedulerState;
  /** 次回切り替え時刻 */
  nextSwitchTime: number | null;
  /** 切り替え間隔（ミリ秒） */
  interval: number;
  /** リトライカウント */
  retryCount: number;
  /** 最大リトライ */
  maxRetries: number;
}

/**
 * システムフラグ
 */
export interface SystemFlags {
  /** 再生中 */
  isPlaying: boolean;
  /** ギフトモード */
  giftMode: boolean;
  /** 初期化完了 */
  initialized: boolean;
}

/**
 * エラー情報
 */
export interface ErrorInfo {
  message: string;
  stack?: string;
  timestamp: number;
  context?: string;
}

/**
 * トランジション履歴レコード
 */
export interface TransitionRecord {
  timestamp: number;
  fromVideo: VideoInfo | null;
  toVideo: VideoInfo;
  duration: number;
  success: boolean;
  error?: string;
}

/**
 * 統一状態データ
 */
export interface UnifiedVideoStateData {
  /** 現在の動画情報 */
  currentVideo: VideoInfo;
  /** プリロード動画情報 */
  preloadVideo: PreloadInfo;
  /** トランジション状態 */
  transition: TransitionState;
  /** スケジューラ状態 */
  scheduler: SchedulerInfo;
  /** システムフラグ */
  system: SystemFlags;
  /** 背景動画キュー */
  backgroundQueue: string[];
  /** 現在のキューインデックス */
  currentQueueIndex: number;
  /** 最新エラー */
  lastError: ErrorInfo | null;
  /** トランジション履歴 */
  history: TransitionRecord[];
}

/**
 * 状態更新パラメータ
 */
export type StateUpdate = DeepPartial<UnifiedVideoStateData>;

/**
 * DeepPartial型
 */
type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 * 状態変更リスナー
 */
export type StateChangeListener = (
  newState: Readonly<UnifiedVideoStateData>,
  previousState: Readonly<UnifiedVideoStateData>,
  changes: StateUpdate
) => void;

// ============================================================================
// UnifiedVideoState Class
// ============================================================================

/**
 * 統一動画状態管理クラス
 */
export class UnifiedVideoState {
  private state: UnifiedVideoStateData;
  private listeners: Set<StateChangeListener>;
  private readonly maxHistory: number;

  constructor(initialState?: Partial<UnifiedVideoStateData>) {
    this.listeners = new Set();
    this.maxHistory = 100;

    // デフォルト状態
    this.state = {
      currentVideo: {
        src: null,
        type: null,
        element: null,
        startTime: null,
        metadata: {}
      },
      preloadVideo: {
        src: null,
        type: null,
        element: null,
        ready: false,
        metadata: {}
      },
      transition: {
        phase: 'idle',
        type: null,
        progress: 0,
        startTime: null,
        name: null,
        error: null
      },
      scheduler: {
        state: 'idle',
        nextSwitchTime: null,
        interval: 30000,
        retryCount: 0,
        maxRetries: 3
      },
      system: {
        isPlaying: false,
        giftMode: false,
        initialized: false
      },
      backgroundQueue: [],
      currentQueueIndex: 0,
      lastError: null,
      history: [],
      ...initialState
    };
  }

  // ===== 状態取得 =====

  /**
   * 状態の取得（読み取り専用）
   */
  getState(): Readonly<UnifiedVideoStateData> {
    return Object.freeze({ ...this.state });
  }

  /**
   * 現在の動画情報を取得
   */
  getCurrentVideo(): Readonly<VideoInfo> {
    return Object.freeze({ ...this.state.currentVideo });
  }

  /**
   * トランジション状態を取得
   */
  getTransitionState(): Readonly<TransitionState> {
    return Object.freeze({ ...this.state.transition });
  }

  /**
   * スケジューラ状態を取得
   */
  getSchedulerState(): Readonly<SchedulerInfo> {
    return Object.freeze({ ...this.state.scheduler });
  }

  /**
   * システムフラグを取得
   */
  getSystemFlags(): Readonly<SystemFlags> {
    return Object.freeze({ ...this.state.system });
  }

  // ===== 状態チェック =====

  /**
   * トランジション可能かチェック
   */
  canTransition(): boolean {
    return this.state.transition.phase === 'idle' ||
           this.state.transition.phase === 'completed';
  }

  /**
   * ギフトモードかチェック
   */
  isGiftMode(): boolean {
    return this.state.system.giftMode;
  }

  /**
   * トランジション中かチェック
   */
  isTransitioning(): boolean {
    return this.state.transition.phase === 'loading' ||
           this.state.transition.phase === 'playing';
  }

  /**
   * スケジューラがアクティブかチェック
   */
  isSchedulerActive(): boolean {
    return this.state.scheduler.state === 'active';
  }

  // ===== 状態更新 =====

  /**
   * 状態の更新（不変更新）
   */
  update(updates: StateUpdate): Readonly<UnifiedVideoStateData> {
    const previousState = this.getState();

    // Deep merge
    this.state = this.deepMergeState(this.state, updates);

    // リスナーに通知
    this.notifyListeners(previousState, updates);

    return this.getState();
  }

  /**
   * トランジション開始
   */
  startTransition(type: TransitionType, name?: string): Readonly<UnifiedVideoStateData> {
    return this.update({
      transition: {
        phase: 'loading',
        type,
        progress: 0,
        startTime: Date.now(),
        name: name || null,
        error: null
      }
    });
  }

  /**
   * トランジション再生中に更新
   */
  setTransitionPlaying(progress: number = 0): Readonly<UnifiedVideoStateData> {
    return this.update({
      transition: {
        phase: 'playing',
        progress
      }
    });
  }

  /**
   * トランジション完了
   */
  completeTransition(success: boolean = true): Readonly<UnifiedVideoStateData> {
    const currentTransition = this.state.transition;
    const duration = currentTransition.startTime
      ? Date.now() - currentTransition.startTime
      : 0;

    // 履歴に追加
    if (this.state.currentVideo.src) {
      this.addToHistory({
        timestamp: Date.now(),
        fromVideo: null,
        toVideo: this.state.currentVideo,
        duration,
        success
      });
    }

    return this.update({
      transition: {
        phase: success ? 'completed' : 'error',
        progress: success ? 1 : this.state.transition.progress,
        error: null
      }
    });
  }

  /**
   * トランジションをリセット（idle状態に戻す）
   */
  resetTransition(): Readonly<UnifiedVideoStateData> {
    return this.update({
      transition: {
        phase: 'idle',
        type: null,
        progress: 0,
        startTime: null,
        name: null,
        error: null
      }
    });
  }

  /**
   * トランジションエラー
   */
  setTransitionError(error: Error): Readonly<UnifiedVideoStateData> {
    return this.update({
      transition: {
        phase: 'error',
        error
      },
      lastError: {
        message: error.message,
        stack: error.stack,
        timestamp: Date.now(),
        context: 'transition'
      }
    });
  }

  /**
   * 動画切り替え
   */
  switchVideo(videoInfo: Partial<VideoInfo>): Readonly<UnifiedVideoStateData> {
    return this.update({
      currentVideo: {
        ...this.state.currentVideo,
        ...videoInfo,
        startTime: Date.now()
      },
      preloadVideo: {
        src: null,
        type: null,
        element: null,
        ready: false,
        metadata: {}
      },
      system: {
        ...this.state.system,
        giftMode: videoInfo.type === 'gift'
      }
    });
  }

  /**
   * スケジューラ状態を更新
   */
  setSchedulerState(state: SchedulerState, nextSwitchTime?: number): Readonly<UnifiedVideoStateData> {
    return this.update({
      scheduler: {
        state,
        nextSwitchTime: nextSwitchTime ?? this.state.scheduler.nextSwitchTime
      }
    });
  }

  /**
   * リトライカウントを増加
   */
  incrementRetryCount(): Readonly<UnifiedVideoStateData> {
    return this.update({
      scheduler: {
        retryCount: this.state.scheduler.retryCount + 1
      }
    });
  }

  /**
   * リトライカウントをリセット
   */
  resetRetryCount(): Readonly<UnifiedVideoStateData> {
    return this.update({
      scheduler: {
        retryCount: 0
      }
    });
  }

  /**
   * ギフトモードを設定
   */
  setGiftMode(enabled: boolean): Readonly<UnifiedVideoStateData> {
    return this.update({
      system: {
        giftMode: enabled
      }
    });
  }

  /**
   * 初期化完了を設定
   */
  setInitialized(initialized: boolean): Readonly<UnifiedVideoStateData> {
    return this.update({
      system: {
        initialized
      }
    });
  }

  /**
   * エラーをクリア
   */
  clearError(): Readonly<UnifiedVideoStateData> {
    return this.update({
      lastError: null,
      transition: {
        error: null
      }
    });
  }

  /**
   * 状態をリセット
   */
  reset(): Readonly<UnifiedVideoStateData> {
    const defaultState = new UnifiedVideoState().state;
    this.state = { ...defaultState, history: this.state.history };
    return this.getState();
  }

  // ===== リスナー管理 =====

  /**
   * リスナー登録
   */
  subscribe(listener: StateChangeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * リスナーに通知
   */
  private notifyListeners(previousState: Readonly<UnifiedVideoStateData>, changes: StateUpdate): void {
    const currentState = this.getState();
    this.listeners.forEach(listener => {
      try {
        listener(currentState, previousState, changes);
      } catch (error) {
        logger.error('State listener error:', error);
      }
    });
  }

  // ===== 履歴管理 =====

  /**
   * 履歴に追加
   */
  private addToHistory(record: TransitionRecord): void {
    this.state.history.push(record);
    if (this.state.history.length > this.maxHistory) {
      this.state.history.shift();
    }
  }

  /**
   * 履歴を取得
   */
  getHistory(): Readonly<TransitionRecord[]> {
    return Object.freeze([...this.state.history]);
  }

  /**
   * 成功率を計算
   */
  getSuccessRate(): number {
    if (this.state.history.length === 0) return 1;
    const successCount = this.state.history.filter(r => r.success).length;
    return successCount / this.state.history.length;
  }

  // ===== ユーティリティ =====

  /**
   * Deep merge helper for UnifiedVideoStateData
   */
  private deepMergeState(target: UnifiedVideoStateData, source: StateUpdate): UnifiedVideoStateData {
    const output: UnifiedVideoStateData = { ...target };

    // currentVideo
    if (source.currentVideo) {
      output.currentVideo = { ...target.currentVideo, ...source.currentVideo };
    }

    // preloadVideo
    if (source.preloadVideo) {
      output.preloadVideo = { ...target.preloadVideo, ...source.preloadVideo };
    }

    // transition
    if (source.transition) {
      output.transition = {
        ...target.transition,
        ...source.transition,
        error: source.transition.error !== undefined ? source.transition.error as (Error | null) : target.transition.error
      };
    }

    // scheduler
    if (source.scheduler) {
      output.scheduler = { ...target.scheduler, ...source.scheduler };
    }

    // system
    if (source.system) {
      output.system = { ...target.system, ...source.system };
    }

    // Simple fields
    if (source.backgroundQueue !== undefined) {
      output.backgroundQueue = source.backgroundQueue;
    }
    if (source.currentQueueIndex !== undefined) {
      output.currentQueueIndex = source.currentQueueIndex;
    }
    if (source.lastError !== undefined) {
      output.lastError = source.lastError as (ErrorInfo | null);
    }
    if (source.history !== undefined) {
      output.history = source.history as TransitionRecord[];
    }

    return output;
  }

  /**
   * Object check helper
   */
  private isObject(item: unknown): item is Record<string, unknown> {
    return item !== null && typeof item === 'object' && !Array.isArray(item);
  }
}

// Export
export default UnifiedVideoState;
